import React, { useCallback, useEffect, useState } from "react";

import { useRouter } from "next/router";

import { t } from "@lingui/macro";
import { Alert, Checkbox, Col, Form, FormInstance, Input, message, Row, Spin, Transfer } from "antd";
import { concat, difference, isEmpty, keyBy } from "lodash";
import styled from "styled-components";
import { KeyedMutator } from "swr";

import { useApplicationContext } from "contexts/app";

import { DriverGroup, UserGroup, VehicleGroup } from "types";

import { isUUID } from "utils/uuid-utils";

const PAGE_SIZE = 1000;

const Container = styled.div`
  padding: ${(props) => {
    return props.theme.sizeMS;
  }}px;
  height: calc(100vh - 135px);
`;

interface ListTransferProps {
  form: FormInstance;
  loading?: boolean;
  group?: DriverGroup & UserGroup & VehicleGroup;
  groupId?: string;
  options?: unknown;
  formView?: string;
  routeName: string;
  listType: string;
  listName: string;
  type: string;
  transferName: string;
  transferNameLabel: string;
  name_display: string;
  nameFieldIndex?: string;
  permissionType: string;
  mutateGroupData?: KeyedMutator<any>;
  callback?: (v: boolean) => void;
}

const ListTransfer = ({
  form,
  loading,
  group,
  groupId,
  formView,
  routeName,
  listType,
  listName,
  type,
  transferName,
  transferNameLabel,
  name_display,
  nameFieldIndex,
  permissionType,
  mutateGroupData,
  callback,
}: ListTransferProps) => {
  const [availableItems, setAvailableItems] = useState<any[]>([]);
  const [transferInputTargetKeys, setTransferInputTargetKeys] = useState<any[] | undefined>([]);
  const [previousChosenItems, setPreviousChosenItems] = useState<any[]>([]);
  const [loadingAvailableDrivers, setLoadingAvailableItems] = useState(false);
  const [loadingPreviousChosenItems, setLoadingPreviousChosenItems] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isValuesChanged, setIsValuesChanged] = useState(false);

  const router = useRouter();
  const { configuration, httpClient } = useApplicationContext();
  const apiUrl = configuration?.environment?.navirec_api_url;

  const accountId = configuration?.account?.id || "";
  const accountUrl = configuration?.account?.url;
  const shouldUpdate = isUUID(groupId);

  const haveManagePermissions = configuration?.account_permissions?.includes(permissionType);

  const { name, active } = group || {};

  const onChange = (nextTargetKeys: any[]) => setTransferInputTargetKeys(nextTargetKeys);

  const getPaginatedData = async (url: string) => {
    let paginatedData: any = {};

    const doRequest = async (requestURL: string) => {
      //TODO: refactor to use fetchAll
      const response = await httpClient.swrInfiniteFetcher(requestURL);
      if (response?.data) {
        // ensures there is no duplicate
        const keyedData = keyBy(response.data, "url");
        paginatedData = { ...paginatedData, ...keyedData };
      }
      const nextURL = response?.cursors?.next?.url;

      if (nextURL) {
        await doRequest(nextURL);
      }
    };

    await doRequest(url);

    return Object.values(paginatedData);
  };

  const getAvailableItems = useCallback(async () => {
    setLoadingAvailableItems(true);
    callback && callback(false);
    const url = new URL(`${apiUrl}${transferName}/`);

    url.searchParams.append("page_size", `${PAGE_SIZE}`);
    url.searchParams.append("account", accountId);
    url.searchParams.append("active", "true");

    if (url?.href) {
      const paginatedData = await getPaginatedData(url.href);
      if (paginatedData) {
        setAvailableItems(concat(availableItems, paginatedData));
      }
    }
    setLoadingAvailableItems(false);
  }, [transferName, groupId, accountId]);

  const getChosenItems = useCallback(async () => {
    if (!groupId || !shouldUpdate) {
      return;
    }

    const url = new URL(`${apiUrl}${transferName}/`);

    if (url) {
      url.searchParams.append(listType, groupId);
      url.searchParams.append("page_size", `${PAGE_SIZE}`);
      url.searchParams.append("account", accountId);
      url.searchParams.append("active", "true");
    }
    if (url?.href) {
      setLoadingPreviousChosenItems(true);
      const paginatedData = await getPaginatedData(url.href);

      const data = concat(previousChosenItems, paginatedData);

      setTransferInputTargetKeys(data.map((item: any) => item?.url));
      setPreviousChosenItems(data);
      setLoadingPreviousChosenItems(false);
    }
  }, [groupId]);

  useEffect(() => {
    if (formView === "overview" && apiUrl && haveManagePermissions) {
      getAvailableItems();
      getChosenItems();
    } else {
      setLoadingAvailableItems(false);
      setLoadingPreviousChosenItems(false);
    }
  }, [groupId, formView, apiUrl, haveManagePermissions]);

  const filterOption = (value: any, option: any) => {
    const inputValue = value.toLowerCase();
    const optionValue = option?.[name_display].toLowerCase();

    return optionValue.indexOf(inputValue) > -1;
  };

  const handleSubmit = async ({ transferData, ...rest }: any) => {
    if (submitting) {
      return;
    }

    setSubmitting(true);

    if (isValuesChanged) {
      try {
        const url = shouldUpdate ? `${listType}/${groupId}/` : `${listType}/`;
        const requestConfiguration = {
          data: {
            ...rest,
            account: accountUrl,
          },
        };

        const response = shouldUpdate
          ? await httpClient.put(url, requestConfiguration)
          : await httpClient.post(url, requestConfiguration);

        if (response) {
          const id = response?.id;
          const name = response?.name;

          const previousChosenItemsURL = previousChosenItems.map((driver) => driver.url);

          if (!transferData) {
            if (previousChosenItemsURL?.length) {
              const addItemUrl = `${listType}/${id}/add/`;

              await httpClient.post(addItemUrl, {
                data: { [transferName]: previousChosenItemsURL },
              });
            }
          } else {
            const transferAdded = difference(transferData, previousChosenItemsURL);
            const transferRemoved = difference(previousChosenItemsURL, transferData);

            if (id && transferAdded?.length) {
              const addItemUrl = `${listType}/${id}/add/`;

              await httpClient.post(addItemUrl, {
                data: { [transferName]: transferAdded },
              });
            }

            if (id && transferRemoved?.length) {
              const removeItemUrl = `${listType}/${id}/remove/`;

              await httpClient.post(removeItemUrl, {
                data: { [transferName]: transferRemoved },
              });
            }
          }

          if (shouldUpdate) {
            mutateGroupData && mutateGroupData();
          }

          message.success(`${type} ${t`was successfully saved`}`, 8);

          if (!shouldUpdate) {
            await router.push(`/settings/${routeName}/${id}/?tab=overview`);
          }
        }
      } finally {
        setSubmitting(false);
        setIsValuesChanged(false);
        callback && callback(false);
      }
    } else {
      message.open({ content: t`No changes to save`, duration: 8, key: "no-changes-to-save", type: "info" });
      setSubmitting(false);
    }

    return null;
  };

  useEffect(() => {
    form.setFieldsValue({
      // @ts-ignore: No index signature with a parameter of type 'string' was found on type
      name: group?.[nameFieldIndex || name_display] || name,
      active: shouldUpdate ? active : true,
    });
  }, [name, active]);

  return (
    <Container>
      <Spin spinning={loading || loadingAvailableDrivers || loadingPreviousChosenItems || submitting}>
        <Form
          form={form}
          name="basic"
          layout="vertical"
          requiredMark={false}
          autoComplete="off"
          disabled={!haveManagePermissions}
          onValuesChange={(changedValues, _) => {
            setIsValuesChanged(!isEmpty(changedValues));
            callback && callback(!isEmpty(changedValues));
          }}
          onFinish={handleSubmit}
        >
          {!haveManagePermissions && (
            <Alert
              message={t`You do not have the permissions to add or edit this ` + " " + listName}
              banner
              type={"warning"}
            />
          )}
          <Row gutter={36}>
            <Col xxl={12} xl={12} lg={12} md={12} sm={24} xs={24}>
              <Form.Item label={listName + " " + t`name`} name="name" required={true}>
                <Input
                  placeholder={t`Enter name`}
                  disabled={!isEmpty(group?.group_type) ? group?.group_type !== "regular" : false}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label={transferNameLabel} name="transferData">
            <Transfer
              dataSource={availableItems}
              showSearch
              filterOption={filterOption}
              titles={[t`Available`, t`Chosen`]}
              rowKey={(record) => record?.url}
              render={(item) => item?.[name_display]}
              listStyle={{
                width: "50%",
                height: "calc(100vh - 330px)",
                minHeight: 400,
              }}
              targetKeys={transferInputTargetKeys}
              onChange={onChange}
              locale={{
                searchPlaceholder: t`Search here`,
                notFoundContent: t`No content`,
                itemUnit: t`item`,
                itemsUnit: t`items`,
              }}
            />
          </Form.Item>
          {listType !== "user_groups" && (
            <Form.Item name="active" valuePropName="checked" noStyle>
              <Checkbox disabled={!haveManagePermissions}>{t`This group is active`}</Checkbox>
            </Form.Item>
          )}
        </Form>
      </Spin>
    </Container>
  );
};

export default ListTransfer;
